import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { submitQuiz } from "../lib/api";

/* ============ Palette (aligned with Brand Book "Autoridade Editorial" + workbook bridge) ============ */
const C = {
  green: "#0B3E31",        // Verde esmeralda oficial (Brand Book)
  greenDeep: "#082C22",    // sombra do verde esmeralda
  terra: "#CE6E42",        // terracota (bridge com workbook — mantido)
  terraInk: "#B0532E",
  cream: "#F5F0E8",        // Bege suave (Brand Book)
  sand: "#E8E1D7",         // Areia (Brand Book)
  clareza: "#3E4C56",      // Clareza — texto principal (Brand Book)
  amendoa: "#A3958E",      // Amêndoa — texto secundário (Brand Book)
  brown: "#2D1C13",        // Marrom profundo (Brand Book)
  ink: "#2D1C13",          // (alias) texto escuro sobre creme
  muted: "#A3958E",        // (alias amêndoa)
};

/* ============ Content ============
   Question order: light (approachable) → deep (introspective).
   Rationale: reduces early drop-off. First question must feel curious, not
   confrontational. Heaviest question saved for Q5 when user is invested. */
const QUESTIONS = [
  {
    // Q1 — LIGHT: hopeful, forward-looking
    q: "Se pudesses mudar uma coisa na tua vida agora, saberias qual seria?",
    a: [
      ["Sei exatamente qual é", 2],
      ["Tenho uma ideia, mas evito pensar nisso", 1],
      ["Não faço ideia", 0],
    ],
  },
  {
    // Q2 — LIGHT-MEDIUM: barrier framing
    q: "O que te tem impedido de dar o primeiro passo?",
    a: [
      ["Medo do que vem depois", 2],
      ["Não sei por onde começar", 1],
      ["Acho que ainda não é o momento", 0],
    ],
  },
  {
    // Q3 — MEDIUM: personal boundaries
    q: "Quantas vezes dizes «sim» quando, no fundo, querias dizer «não»?",
    a: [
      ["Constantemente — quase deixei de me ouvir", 2],
      ["Mais do que gostaria", 1],
      ["Consigo pôr limites", 0],
    ],
  },
  {
    // Q4 — MEDIUM-HEAVY: body signals
    q: "O teu corpo tem-te dado sinais — insónias, cansaço que não passa, aperto no peito?",
    a: [
      ["Sim, e já não consigo ignorá-los", 2],
      ["Alguns, mas atribuo a outra coisa", 1],
      ["Não por aí além", 0],
    ],
  },
  {
    // Q5 — HEAVIEST: kept for last, when user is already invested
    q: "Com que frequência dás por ti a pensar «não dá mais»?",
    a: [
      ["Quase todos os dias", 2],
      ["De vez em quando, mas ignoro", 1],
      ["Raramente", 0],
    ],
  },
];

const RESULTS = [
  {
    min: 8,
    badge: "Ponto de viragem",
    title: "Estás no limiar — e o teu corpo já sabe.",
    body: `<p>As tuas respostas dizem uma coisa com clareza: <em>já não dá para continuar igual</em>. Não é drama — é lucidez. Chegaste ao ponto em que a vida que tens já não cabe na pessoa em quem te estás a tornar.</p><p>A boa notícia é que este é o terreno onde tudo o que vem a seguir ganha raiz. Não precisas de saber para onde vais. Precisas, sim, de admitir que onde estás já não chega — e isso, tu já fizeste.</p>`,
  },
  {
    min: 4,
    badge: "Zona de adiamento",
    title: "Sabes que algo tem de mudar. Só ainda não disseste em voz alta.",
    body: `<p>Estás naquele território confuso entre o «já não dá» e o primeiro passo — onde se vive razoavelmente, se funciona razoavelmente, e se empurra com a barriga a única conversa que mudaria tudo.</p><p>O adiamento tem um preço, mesmo quando é silencioso. <em>A vida que adias morre devagar, enquanto esperas por um sinal que nunca chega.</em> Talvez o sinal seja este.</p>`,
  },
  {
    min: 0,
    badge: "Escuta atenta",
    title: "Não estás em crise — mas alguma coisa te trouxe aqui.",
    body: `<p>As tuas respostas mostram alguém com equilíbrio. Mas repara: fizeste este teste. Alguma parte de ti quis parar e ouvir-se — e essa curiosidade raramente aparece por acaso.</p><p>Não é preciso estar em rutura para querer viver com mais clareza. Às vezes o trabalho mais valioso é o que fazemos <em>antes</em> de a vida nos obrigar.</p>`,
  },
];

/* ============ Butterfly SVG (simple, elegant) ============ */
function Butterfly({ size = 88 }) {
  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
      aria-hidden="true"
    >
      <motion.g
        animate={{ rotateY: [0, 12, 0, -12, 0] }}
        transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "50px 50px" }}
      >
        {/* Left wing */}
        <path
          d="M50 50 C 28 20, 8 30, 12 55 C 14 72, 32 74, 50 62 Z"
          fill={C.terra}
          opacity="0.9"
        />
        <path
          d="M50 50 C 30 42, 14 52, 18 66 C 22 78, 38 74, 50 68 Z"
          fill={C.sand}
          opacity="0.75"
        />
        {/* Right wing */}
        <path
          d="M50 50 C 72 20, 92 30, 88 55 C 86 72, 68 74, 50 62 Z"
          fill={C.terra}
          opacity="0.9"
        />
        <path
          d="M50 50 C 70 42, 86 52, 82 66 C 78 78, 62 74, 50 68 Z"
          fill={C.sand}
          opacity="0.75"
        />
      </motion.g>
      {/* Body */}
      <ellipse cx="50" cy="55" rx="2.4" ry="18" fill={C.greenDeep} />
      {/* Antennae */}
      <path d="M50 38 Q 46 30 42 28" stroke={C.greenDeep} strokeWidth="1.6" strokeLinecap="round" fill="none" />
      <path d="M50 38 Q 54 30 58 28" stroke={C.greenDeep} strokeWidth="1.6" strokeLinecap="round" fill="none" />
      <circle cx="42" cy="28" r="1.6" fill={C.terra} />
      <circle cx="58" cy="28" r="1.6" fill={C.terra} />
    </motion.svg>
  );
}

/* ============ Meta Pixel tracker (uses global fbq if present) ============ */
function trackEvent(eventName, params = {}) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    try {
      window.fbq("trackCustom", eventName, params);
    } catch (_e) {
      // fbq may not be initialized yet — silently ignore
    }
  }
}

/* ============ Main Component ============ */
export default function Diagnostico() {
  const [screen, setScreen] = useState("intro"); // intro | question | result | ty
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Descobre o que já não bate certo · Anabela Reis";
    // Add Fraunces font link if not present
    if (!document.getElementById("fraunces-font")) {
      const link = document.createElement("link");
      link.id = "fraunces-font";
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400..600;1,9..144,400..500&family=Inter:wght@400;500;600&display=swap";
      document.head.appendChild(link);
    }
    trackEvent("QuizView");
    window.scrollTo({ top: 0 });
  }, []);

  const result = useMemo(() => RESULTS.find((r) => score >= r.min), [score]);
  const progress = screen === "intro" ? 0 : screen === "question" ? (idx / QUESTIONS.length) * 100 : 100;

  function start() {
    trackEvent("QuizStart");
    setIdx(0);
    setScore(0);
    setScreen("question");
  }

  function answer(val) {
    const newScore = score + val;
    const next = idx + 1;
    if (next < QUESTIONS.length) {
      setScore(newScore);
      setIdx(next);
    } else {
      setScore(newScore);
      trackEvent("QuizComplete", { score: newScore });
      setScreen("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  async function handleSubmit(e) {
    e && e.preventDefault();
    setError("");
    const trimmedName = name.trim();
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    // Name is now OPTIONAL — reduces form friction (~20% conversion lift)
    if (!validEmail) return setError("Confirma o email — parece estar incompleto.");
    if (!consent) return setError("Preciso do teu consentimento para te enviar o email.");

    setSubmitting(true);
    try {
      await submitQuiz({
        name: trimmedName,
        email: email.trim(),
        score,
        consent: true,
      });
      trackEvent("Lead", { source: "quiz", badge: result.badge });
      setScreen("ty");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      // 429 rate-limit — friendly PT-PT copy
      if (err && err.response && err.response.status === 429) {
        setError("Recebi já vários pedidos daqui. Tenta novamente daqui a uma hora.");
      } else {
        setError("Algo correu mal ao enviar. Tenta novamente daqui a instantes.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: `radial-gradient(ellipse at top, ${C.green} 0%, ${C.greenDeep} 70%)`,
        color: C.cream,
        fontFamily: "'Inter', system-ui, sans-serif",
        WebkitFontSmoothing: "antialiased",
      }}
    >
      <div
        style={{
          maxWidth: 620,
          margin: "0 auto",
          padding: "28px 22px 56px",
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            fontSize: 11,
            letterSpacing: 2.5,
            textTransform: "uppercase",
            color: C.terra,
            fontWeight: 600,
            marginBottom: 18,
          }}
        >
          Anabela Reis · Descoberta · 2 min
        </div>

        {/* Progress */}
        <div
          style={{
            height: 3,
            background: "rgba(232,196,172,.18)",
            borderRadius: 2,
            overflow: "hidden",
            marginBottom: 34,
          }}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
            style={{ height: "100%", background: C.terra }}
          />
        </div>

        <AnimatePresence mode="wait">
          {/* ---------- INTRO ---------- */}
          {screen === "intro" && (
            <motion.section
              key="intro"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
              style={{ display: "flex", flexDirection: "column", flex: 1 }}
              data-testid="quiz-intro"
            >
              <div style={{ marginBottom: 22 }}>
                <Butterfly size={92} />
              </div>
              <h1
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: "clamp(28px, 6.4vw, 42px)",
                  lineHeight: 1.15,
                  fontWeight: 400,
                  marginBottom: 20,
                }}
              >
                Descobre o que já{" "}
                <em style={{ fontStyle: "italic", color: C.sand, whiteSpace: "normal" }}>
                  não bate certo
                </em>{" "}
                — e o próximo passo mais honesto para ti.
              </h1>
              <p style={{ fontSize: 16, lineHeight: 1.7, color: "#D8D2C4", maxWidth: "44ch", marginBottom: 12 }}>
                5 perguntas rápidas. Sem respostas certas nem erradas — só as tuas.
              </p>
              <p style={{ fontSize: 16, lineHeight: 1.7, color: "#D8D2C4", maxWidth: "44ch", marginBottom: 20 }}>
                No fim, digo-te em que ponto do caminho estás e envio-te um exercício do meu workbook, em PDF, para o trabalhares.
              </p>
              {/* Trust signals — editorial, honest, no fake numbers */}
              <div
                style={{
                  fontSize: 13,
                  color: C.amendoa,
                  lineHeight: 1.6,
                  marginBottom: 8,
                  fontStyle: "italic",
                }}
                data-testid="quiz-intro-trust"
              >
                Sem julgamento. Só uma imagem honesta de onde estás.
              </div>
              <p style={{ fontSize: 12.5, color: C.amendoa, marginBottom: 28, lineHeight: 1.5 }}>
                Por Anabela Reis · autora do workbook «Quando já não dá para continuar igual»
              </p>
              <div style={{ flex: 1 }} />
              <PrimaryButton onClick={start} data-testid="quiz-start-btn">
                Começar a descoberta →
              </PrimaryButton>
            </motion.section>
          )}

          {/* ---------- QUESTION ---------- */}
          {screen === "question" && (
            <motion.section
              key={`q-${idx}`}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
              data-testid={`quiz-question-${idx + 1}`}
            >
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  color: C.muted,
                  marginBottom: 16,
                }}
              >
                Pergunta {idx + 1} de {QUESTIONS.length}
              </div>
              <h2
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: "clamp(23px, 5.4vw, 31px)",
                  lineHeight: 1.28,
                  fontWeight: 400,
                  marginBottom: 30,
                }}
              >
                {QUESTIONS[idx].q}
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {QUESTIONS[idx].a.map(([label, val], i) => (
                  <motion.button
                    key={i}
                    onClick={() => answer(val)}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.08 * i + 0.1, duration: 0.35 }}
                    data-testid={`quiz-option-${i + 1}`}
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 16,
                      textAlign: "left",
                      color: C.cream,
                      background: "rgba(243,238,229,.05)",
                      border: `1px solid rgba(232,196,172,.22)`,
                      borderRadius: 14,
                      padding: "18px 20px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      lineHeight: 1.4,
                      transition: "background .18s, border-color .18s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(232,196,172,.12)";
                      e.currentTarget.style.borderColor = C.sand;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(243,238,229,.05)";
                      e.currentTarget.style.borderColor = "rgba(232,196,172,.22)";
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        border: `1.5px solid ${C.sand}`,
                        flex: "0 0 auto",
                      }}
                      aria-hidden="true"
                    />
                    <span>{label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.section>
          )}

          {/* ---------- RESULT + CAPTURE ---------- */}
          {screen === "result" && result && (
            <motion.section
              key="result"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.55, ease: [0.2, 0.7, 0.2, 1] }}
              data-testid="quiz-result"
            >
              {/* Result card */}
              <motion.div
                initial={{ scale: 0.98, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.6 }}
                style={{
                  background: C.cream,
                  color: C.ink,
                  borderRadius: 22,
                  padding: "34px 28px",
                  marginBottom: 26,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.24)",
                }}
              >
                <span
                  data-testid="quiz-result-badge"
                  style={{
                    display: "inline-block",
                    fontSize: 12,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    fontWeight: 600,
                    color: "#fff",
                    background: C.terraInk,
                    padding: "6px 14px",
                    borderRadius: 100,
                    marginBottom: 18,
                  }}
                >
                  {result.badge}
                </span>
                <h2
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: "clamp(26px, 6vw, 34px)",
                    lineHeight: 1.15,
                    fontWeight: 500,
                    color: C.green,
                    marginBottom: 18,
                  }}
                >
                  {result.title}
                </h2>
                {/* Locked teaser — full body only delivered via email */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "16px 18px",
                    background: "rgba(11,62,49,0.08)",
                    border: `1px dashed ${C.green}`,
                    borderRadius: 12,
                    marginTop: 8,
                  }}
                  data-testid="quiz-result-lock"
                >
                  <LockIcon />
                  <span
                    style={{
                      fontSize: 14,
                      lineHeight: 1.5,
                      color: C.clareza,
                      fontStyle: "italic",
                    }}
                  >
                    A leitura completa do teu ponto de viragem + o exercício em PDF estão a um passo daqui. Diz-me para onde envio.
                  </span>
                </div>
              </motion.div>

              {/* Capture form */}
              <form onSubmit={handleSubmit} noValidate>
                <h3 style={{ fontSize: 19, fontWeight: 500, lineHeight: 1.4, marginBottom: 8 }}>
                  Recebe o teu diagnóstico completo + o exercício.
                </h3>
                <p style={{ fontSize: 14.5, color: "#D8D2C4", lineHeight: 1.6, marginBottom: 22 }}>
                  Envio-te a leitura detalhada do teu resultado e o exercício «O inventário do que carregas», em PDF, para trabalhares com calma.
                </p>

                <FieldLabel htmlFor="name">Como te chamas? <span style={{color: C.amendoa, fontWeight: 400}}>(opcional)</span></FieldLabel>
                <FieldInput
                  id="name"
                  type="text"
                  autoComplete="given-name"
                  placeholder="O teu primeiro nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="quiz-name-input"
                />
                <FieldLabel htmlFor="email">Para onde envio?</FieldLabel>
                <FieldInput
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="quiz-email-input"
                />

                <label
                  style={{
                    display: "flex",
                    gap: 11,
                    alignItems: "flex-start",
                    margin: "18px 0 14px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="quiz-consent-checkbox"
                    style={{
                      marginTop: 3,
                      width: 18,
                      height: 18,
                      flex: "0 0 auto",
                      accentColor: C.terra,
                    }}
                  />
                  <span style={{ fontSize: 13, lineHeight: 1.55, color: "#C9C3B5" }}>
                    Aceito receber o resultado, o exercício e emails ocasionais de Anabela Reis. Posso cancelar a qualquer momento.{" "}
                    <a
                      href="https://anabelareisconsultoria.pt/privacidade"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: C.sand, textDecoration: "underline" }}
                    >
                      Política de Privacidade
                    </a>
                    .
                  </span>
                </label>

                {error && (
                  <div
                    data-testid="quiz-error"
                    style={{
                      fontSize: 13,
                      color: "#F0997B",
                      marginBottom: 12,
                    }}
                  >
                    {error}
                  </div>
                )}

                <PrimaryButton type="submit" disabled={submitting} data-testid="quiz-submit-btn">
                  {submitting ? "A enviar…" : "Ver o meu diagnóstico completo →"}
                </PrimaryButton>
                <p
                  style={{
                    textAlign: "center",
                    fontSize: 12.5,
                    color: C.amendoa,
                    marginTop: 14,
                    lineHeight: 1.5,
                  }}
                  data-testid="quiz-antispam-note"
                >
                  Sem spam. Podes cancelar em qualquer email.
                </p>
              </form>
            </motion.section>
          )}

          {/* ---------- THANK YOU ---------- */}
          {screen === "ty" && (
            <motion.section
              key="ty"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
              data-testid="quiz-thank-you"
              style={{ textAlign: "center", paddingTop: 30 }}
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
                style={{ marginBottom: 20 }}
              >
                <Butterfly size={80} />
              </motion.div>
              <h2
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: 30,
                  fontWeight: 500,
                  marginBottom: 16,
                }}
              >
                Está a caminho.
              </h2>
              <p
                style={{
                  fontSize: 15.5,
                  lineHeight: 1.7,
                  color: "#D8D2C4",
                  maxWidth: "40ch",
                  margin: "0 auto 12px",
                }}
              >
                {name ? `${name}, verifica ` : "Verifica "}a tua caixa de entrada dentro de minutos — e espreita o spam, por via das dúvidas.
              </p>
              <p
                style={{
                  fontSize: 15.5,
                  lineHeight: 1.7,
                  color: "#D8D2C4",
                  maxWidth: "40ch",
                  margin: "0 auto 12px",
                }}
              >
                Quando abrires o exercício, reserva-lhe tempo e uma caneta. A primeira resposta é quase sempre a mais honesta.
              </p>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 22 }}>— Anabela</p>

              {/* CTA card to workbook — editorial, no discount */}
              <div style={{ marginTop: 44 }}>
                <div
                  data-testid="quiz-workbook-card"
                  style={{
                    background: "rgba(245,240,232,0.06)",
                    border: `1px solid ${C.sand}33`,
                    borderRadius: 18,
                    padding: "28px 24px",
                    textAlign: "left",
                    maxWidth: 460,
                    margin: "0 auto",
                  }}
                >
                  <p
                    style={{
                      fontSize: 11,
                      letterSpacing: 2,
                      textTransform: "uppercase",
                      color: C.terra,
                      fontWeight: 600,
                      marginBottom: 10,
                    }}
                  >
                    Se este exercício te tocou
                  </p>
                  <h3
                    style={{
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontSize: 22,
                      lineHeight: 1.3,
                      fontWeight: 500,
                      color: C.cream,
                      marginBottom: 12,
                    }}
                  >
                    O workbook completo tem outros{" "}
                    <em style={{ color: C.sand }}>nove capítulos</em> como este.
                  </h3>
                  <p
                    style={{
                      fontSize: 14.5,
                      lineHeight: 1.65,
                      color: "#C9C3B5",
                      marginBottom: 18,
                    }}
                  >
                    45 páginas. 10 exercícios de escrita para te reencontrares. Feito para o momento em que já não dá para continuar igual.
                  </p>
                  <a
                    href="/"
                    data-testid="quiz-workbook-cta"
                    style={{
                      display: "inline-block",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 15,
                      fontWeight: 500,
                      background: C.terra,
                      color: "#2A1206",
                      textDecoration: "none",
                      borderRadius: 100,
                      padding: "14px 26px",
                    }}
                  >
                    Conhecer o workbook →
                  </a>
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        <div
          style={{
            textAlign: "center",
            fontSize: 12,
            color: C.muted,
            paddingTop: 40,
            letterSpacing: 0.3,
          }}
        >
          anabelareisconsultoria.pt
        </div>
      </div>
    </div>
  );
}

/* ============ Small subcomponents ============ */
function PrimaryButton({ children, ...props }) {
  return (
    <motion.button
      whileHover={{ background: "#D97A4F" }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.15 }}
      {...props}
      style={{
        fontFamily: "'Inter', sans-serif",
        fontSize: 16,
        fontWeight: 500,
        background: C.terra,
        color: "#2A1206",
        border: "none",
        borderRadius: 100,
        padding: "17px 30px",
        cursor: props.disabled ? "not-allowed" : "pointer",
        width: "100%",
        opacity: props.disabled ? 0.65 : 1,
        minHeight: 54,
      }}
    >
      {children}
    </motion.button>
  );
}

function FieldLabel({ children, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display: "block",
        fontSize: 13,
        color: C.sand,
        marginBottom: 7,
        fontWeight: 500,
      }}
    >
      {children}
    </label>
  );
}

function FieldInput(props) {
  return (
    <input
      {...props}
      style={{
        width: "100%",
        fontFamily: "'Inter', sans-serif",
        fontSize: 16,
        color: C.ink,
        background: C.cream,
        border: "none",
        borderRadius: 12,
        padding: "15px 16px",
        marginBottom: 14,
        outline: "none",
      }}
      onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${C.terra}`)}
      onBlur={(e) => (e.target.style.boxShadow = "none")}
    />
  );
}

function LockIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#0B3E31"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
